import React, { useState, useEffect } from 'react';
import { DollarSign, Hash, TrendingDown } from 'lucide-react';
import apiService from '../../services/apiService';
import { formatMoney } from '../../utils/formatters';
import './PricingByQuantity.css';

const PricingByQuantity = ({
    producto,
    cantidad = 1,
    cliente_id = null,
    onPriceChange,
    showRangeBreakdown = false,
    className = ''
}) => {
    const [pricingInfo, setPricingInfo] = useState(null);
    const [rangosDisponibles, setRangosDisponibles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (producto?.id && cantidad > 0) {
            calculatePricing();
        }
    }, [producto, cantidad, cliente_id]);

    useEffect(() => {
        if (showRangeBreakdown) {
            loadRangosDisponibles();
        }
    }, [showRangeBreakdown]);

    const calculatePricing = async () => {
        try {
            setLoading(true);
            setError(null);

            const result = await apiService.getPrecioPorCantidad(producto.id, cantidad, cliente_id);

            if (result.success) {
                setPricingInfo(result.data);

                // Notificar al componente padre del cambio de precio
                if (onPriceChange) {
                    onPriceChange({
                        precio_unitario: result.data.precio_final,
                        precio_total: result.data.total,
                        precio_tipo: result.data.precio_tipo,
                        descuento_aplicado: result.data.porcentaje_descuento
                    });
                }
            } else {
                // Si no hay configuración de rangos, usar precio_lista como base
                const precio_base = producto.precio_lista || producto.precio1 || 0;
                const fallbackPricing = {
                    producto_id: producto.id,
                    producto_nombre: producto.nombre,
                    cantidad: cantidad,
                    precio_tipo: 'precio_lista',
                    precio_base: precio_base,
                    porcentaje_descuento: 0,
                    precio_final: precio_base,
                    total: precio_base * cantidad
                };

                setPricingInfo(fallbackPricing);

                if (onPriceChange) {
                    onPriceChange({
                        precio_unitario: precio_base,
                        precio_total: precio_base * cantidad,
                        precio_tipo: 'precio_lista',
                        descuento_aplicado: 0
                    });
                }
            }
        } catch (error) {
            setError('Error al calcular precio');
            console.error('Error calculating pricing:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadRangosDisponibles = async () => {
        try {
            const result = await apiService.getConfiguracionRangos();
            if (result.success) {
                setRangosDisponibles(result.data);
            }
        } catch (error) {
            console.error('Error loading price ranges:', error);
        }
    };

    const getPrecioTipoColor = (tipo) => {
        switch (tipo) {
            case 'precio_lista': return '#0284c7';
            case 'precio1': return '#7c3aed';
            case 'precio2': return '#16a34a';
            case 'precio3': return '#ca8a04';
            case 'precio4': return '#dc2626';
            case 'precio5': return '#0891b2';
            case 'precio6': return '#7e22ce';
            default: return '#0284c7';
        }
    };

    const formatPrecioTipo = (tipo) => {
        switch (tipo) {
            case 'precio_lista': return 'Precio Lista';
            case 'precio1': return 'Precio 1';
            case 'precio2': return 'Precio 2';
            case 'precio3': return 'Precio 3';
            case 'precio4': return 'Precio 4';
            case 'precio5': return 'Precio 5';
            case 'precio6': return 'Precio 6';
            default: return tipo;
        }
    };

    const contarPreciosConfigurados = () => {
        if (!producto) return 0;

        let count = 0;
        if (parseFloat(producto.precio1 || 0) > 0) count++;
        if (parseFloat(producto.precio2 || 0) > 0) count++;
        if (parseFloat(producto.precio3 || 0) > 0) count++;
        if (parseFloat(producto.precio4 || 0) > 0) count++;
        if (parseFloat(producto.precio5 || 0) > 0) count++;
        if (parseFloat(producto.precio6 || 0) > 0) count++;

        return count;
    };

    if (loading) {
        return (
            <div className={`pricing-by-quantity ${className}`}>
                <div className="pricing-loading">
                    <div className="loading-spinner-small"></div>
                    <span>Calculando precio...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={`pricing-by-quantity error ${className}`}>
                <span className="error-text">{error}</span>
            </div>
        );
    }

    if (!pricingInfo) {
        return null;
    }

    return (
        <div className={`pricing-by-quantity ${className}`}>
            <div className="pricing-main">
                <div className="pricing-header">
                    <div className="pricing-header-left">
                        <div className="quantity-info">
                            <Hash size={14} />
                            <span>{cantidad} {producto.unidad_medida || 'pz'}</span>
                        </div>

                        <div className="precios-configurados-badge">
                            <DollarSign size={12} />
                            <span>{contarPreciosConfigurados()}/6 precios</span>
                        </div>
                    </div>

                    <div
                        className="price-type-badge"
                        style={{ backgroundColor: getPrecioTipoColor(pricingInfo.precio_tipo) }}
                    >
                        {formatPrecioTipo(pricingInfo.precio_tipo)}
                    </div>
                </div>

                <div className="pricing-details">
                    <div className="unit-price">
                        <span className="price-label">Precio unitario:</span>
                        <div className="price-value">
                            {pricingInfo.porcentaje_descuento > 0 && (
                                <span className="original-price">
                                    {formatMoney(pricingInfo.precio_base)}
                                </span>
                            )}
                            <span className="final-price">
                                {formatMoney(pricingInfo.precio_final)}
                            </span>
                        </div>
                    </div>

                    {pricingInfo.porcentaje_descuento > 0 && (
                        <div className="discount-info">
                            <TrendingDown size={14} />
                            <span>Descuento: {pricingInfo.porcentaje_descuento}%</span>
                        </div>
                    )}

                    <div className="total-price">
                        <span className="total-label">Total:</span>
                        <span className="total-value">
                            {formatMoney(pricingInfo.total)}
                        </span>
                    </div>
                </div>
            </div>

            {showRangeBreakdown && rangosDisponibles.length > 0 && (
                <div className="pricing-breakdown">
                    <h5 className="breakdown-title">Rangos de Precio Disponibles</h5>
                    <div className="ranges-list">
                        {rangosDisponibles.map((rango) => {
                            const isActive = rango.precio_tipo === pricingInfo.precio_tipo;
                            const precio_producto = producto[rango.precio_tipo] || 0;
                            const precio_con_descuento = precio_producto * (1 - (rango.porcentaje_descuento / 100));

                            return (
                                <div
                                    key={rango.id}
                                    className={`range-item ${isActive ? 'active' : ''}`}
                                >
                                    <div className="range-info">
                                        <div className="range-quantity">
                                            {rango.cantidad_minima} - {rango.cantidad_maxima || '∞'} unidades
                                        </div>
                                        <div className="range-name">{rango.nombre}</div>
                                    </div>

                                    <div className="range-pricing">
                                        <div
                                            className="range-type"
                                            style={{ color: getPrecioTipoColor(rango.precio_tipo) }}
                                        >
                                            {formatPrecioTipo(rango.precio_tipo)}
                                        </div>
                                        <div className="range-price">
                                            {formatMoney(precio_con_descuento)}
                                            {rango.porcentaje_descuento > 0 && (
                                                <span className="range-discount">
                                                    (-{rango.porcentaje_descuento}%)
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PricingByQuantity;